import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError, toastSuccess } from '@components/Toast';

import useSingleFileUpload from '@hooks/useSingleFileUpload';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	addSupplierDocument,
	addSupplierSafetyDocument,
	getSupplierDocuments,
	removeSupplierDocuments
} from '@services/supplierOnboardingService';

import { getFileType, getIconByFileType, printApiError } from '@utils/Util';

import { documentTypes } from '@/constants/constant';

const SupplierDocumentUpload = ({
	title,
	fieldName,
	uploadType = 'SUPPLIER_DOCUMENT',
	multiple = true,
	callBack,
	documents
}) => {
	const { id } = useParams();
	const { readFile } = useSingleFileUpload();
	const dispatch = useDispatch();

	const onRemoveDocument = async (docId) => {
		try {
			dispatch(openLoader());
			if (['SUPPORTING_DOCUMENT', 'SUPPLIER_DOCUMENT'].includes(uploadType)) {
				await removeSupplierDocuments(id, docId);
				await dispatch(getSupplierDocuments(id));
			}
			if (uploadType === 'SAFETY_INFORMATION') {
				await removeSupplierDocuments(id, docId);
				await callBack();
			}
			dispatch(closeLoader());
		} catch (e) {
			dispatch(closeLoader());
			printApiError(e);
		}
	};

	const renderTypeWiseDocuments = () => {
		const documentList = documents[fieldName] || [];
		if (documentList.length > 0) {
			return documentList?.map((item) => {
				let fileType;
				if (item.docUrl) {
					fileType = getFileType(item.docUrl);
				} else {
					fileType = getFileType(item.name);
				}

				return (
					<div key={`document_${item.id}`} className="addition-info d-flex align-items-center">
						<img className="file-icon" src={getIconByFileType(fileType)} width={20} alt="" />
						<p className="regular-12" onClick={() => window.open(item.docUrl, '_blank')}>
							{item.name}
						</p>
						<div className="remove-icon cursor-pointer" onClick={() => onRemoveDocument(item.id)}>
							<img src={`${process.env.PUBLIC_URL}/icons/trash.svg`} alt="" width={20} />
						</div>
					</div>
				);
			});
		}
	};

	const handleFileChange = async (e) => {
		try {
			const files = e.target.files;
			if (!files) {
				toastError('No files selected!');
				return;
			}
			const readFilePromises = Array.from(files).map((file) =>
				readFile(file).then((base64Str) => {
					let documentType = fieldName;
					if (['SUPPLIER_DOCUMENT'].includes(uploadType)) {
						documentType = documentTypes[fieldName];
					} else if (uploadType === 'SAFETY_INFORMATION') {
						documentType = 'SUPPLIER_SAFETY_DOCUMENT';
					} else if (uploadType === 'SUPPORTING_DOCUMENT') {
						documentType = 'SUPPLIER_SUPPORTING_DOCUMENT';
					}
					return {
						name: file.name,
						docMimeType: file.type,
						documentType: documentType,
						base64Str
					};
				})
			);

			const data = await Promise.all(readFilePromises);
			if (uploadType === 'SUPPLIER_DOCUMENT') {
				await addSupplierDocument(id, data);
				await dispatch(getSupplierDocuments(id));
			}
			if (['SAFETY_INFORMATION'].includes(uploadType)) {
				await addSupplierSafetyDocument(id, { documentDTO: data[0], safetyDocumentType: fieldName });
			}
			if (['SUPPORTING_DOCUMENT'].includes(uploadType)) {
				await addSupplierSafetyDocument(id, {
					documentDTO: data[0],
					supportingDocumentType: fieldName
				});
			}
			if (callBack) {
				await callBack();
			}
			toastSuccess('Updated successfully');
		} catch {
			toastError('Failed to upload file!');
		}
	};

	return (
		<div className="add-file pb-3">
			<p className="regular-14 gray_dark_02 pb-2">{title}</p>
			<div className="d-flex flex-wrap">
				<div className="inline-upload pr-12 ">
					<label htmlFor={fieldName}>
						<img src={`${process.env.PUBLIC_URL}/icons/upload-dark.svg`} />
						Upload file
					</label>
					<input
						type="file"
						className="file-upload d-none"
						id={fieldName}
						name={fieldName}
						onClick={(e) => (e.target.value = null)}
						multiple={multiple}
						onChange={handleFileChange}
					/>
				</div>
				{renderTypeWiseDocuments()}
			</div>
		</div>
	);
};

export default SupplierDocumentUpload;
